// Route guard: signed in, approved, and with at least the role the page needs.
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '../domain/types';
import { useI18n } from '../i18n/I18nProvider';
import { useAuth } from './AuthProvider';

/** `collect`: the page is open to collectors (every other page sends them to /collect). */
export function RequireAuth({ role, collect, children }: { role?: Role; collect?: boolean; children: ReactNode }) {
  const auth = useAuth();
  const { t } = useI18n();
  const loc = useLocation();

  if (auth.status === 'loading') {
    return (
      <div className="uco-boot">
        <span className="uco-spin" />
        {t('loading')}
      </div>
    );
  }
  if (auth.status === 'signedOut') {
    return <Navigate to={'/login?next=' + encodeURIComponent(loc.pathname)} replace />;
  }
  if (auth.status === 'error') {
    return (
      <div className="uco-gate">
        <div className="uco-gate-card">
          <h2>{t('dbError')}</h2>
          <p className="err">{auth.error}</p>
        </div>
      </div>
    );
  }
  if (auth.status === 'pending') {
    return (
      <div className="uco-gate pg">
        <div className="uco-gate-card">
          <h2>{t('waitTitle')}</h2>
          <p>{t('waitBody', { email: auth.user?.email || '' })}</p>
          <div className="uco-row">
            <button className="primary" onClick={() => void auth.refresh()}>
              {t('waitRetry')}
            </button>
            <button onClick={() => void auth.signOut()}>{t('signOut')}</button>
          </div>
        </div>
      </div>
    );
  }
  if (auth.isCollector && !collect) return <Navigate to="/collect" replace />;
  if (role && !auth.hasRole(role)) return <Navigate to="/history" replace />;
  return <>{children}</>;
}

export function ReadOnlyBanner() {
  const { canWrite } = useAuth();
  const { t } = useI18n();
  if (canWrite) return null;
  return <div className="uco-banner">{t('readOnly')}</div>;
}
